import {
  Link as BaseLink,
  LinkProps as BaseLinkProps,
  useRecipe,
} from "@chakra-ui/react";
import type { LinkSharedProps } from "./Link.props";

export type LinkProps = LinkSharedProps & BaseLinkProps;

export default function Link({
  children,
  size,
  variant = "plain",
  colorPalette = "primary",
  isExternal,
  external,
  sx,
  css,
  color,
  ...rest
}: LinkProps): JSX.Element {
  // Get recipe styles
  const recipe = useRecipe({ key: "link" });
  const recipeStyles = recipe({ variant });

  // Map v1/v2 props to v3 props for backward compatibility
  const externalValue = external !== undefined ? external : isExternal;

  const { target, rel, ...restProps } = rest;
  const targetValue = externalValue ? "_blank" : target;
  const relValue = externalValue ? rel ?? "noopener noreferrer" : rel;
  const mergedCss =
    sx && css ? ([css, sx] as unknown as BaseLinkProps["css"]) : sx ?? css;
  const sizeParams = () => {
    switch (size) {
      case "sm":
        return {
          fontSize: "14px",
        };
      case "md":
      default:
        return {
          fontSize: "16px",
        };
    }
  };

  return (
    <BaseLink
      {...sizeParams()}
      colorPalette={colorPalette}
      target={targetValue}
      rel={relValue}
      css={[recipeStyles, mergedCss]}
      {...(color && { color })}
      {...restProps}
    >
      {children}
    </BaseLink>
  );
}

/**
 * @deprecated Use `<Link variant="underline">` instead or <Button.Ghost> or <Button.Link>
 */
Link.Outline = function Outline({ children, ...rest }: LinkProps) {
  return (
    <Link
      display="inline-block"
      border="1px solid"
      borderColor="primary.800"
      borderRadius="sm"
      color="primary.800"
      padding="xs"
      _hover={{
        textDecoration: "none",
        backgroundColor: "primary.800",
        color: "white",
      }}
      {...rest}
    >
      {children}
    </Link>
  );
};
